import { apiClient } from 'utils/ApiClient';
import { Logger } from 'utils/Logger';
import type { CricketWidgetPayload, ScreenWidget, StockQuotesPayload } from '../../../components/ScreenComposer/composer.types';
import {
    NewsFeedPreview,
    WeatherPreview,
    PlaybackLogRow,
    Screen,
    ScreenClientRelease,
    ScreenCommand,
    ScreenCommandType,
    ScreenOrientation,
    ScreenLogLevel,
    ScreenSettings,
    ScreensResponse,
} from './Screens.types';

const LOG_PREFIX = 'BeamOnScreen/screens/Screens.api';

const id = (v: string) => encodeURIComponent(v);

export type LiveViewQuality = '480p' | '720p' | '1080p';

export interface LiveViewState {
    enabled: boolean;
    quality: LiveViewQuality;
    enabledAt: string | null;
    autoOffAt: string | null;
    autoOffSec: number;
    turnedOffAutomatically: boolean;
    updatedAt: string | null;
    updatedBy: string;
    online: boolean;
    device: {
        state?: string;
        quality?: string;
        since?: string | null;
        autoOffAt?: string | null;
        lastError?: string;
    };
    qualities: LiveViewQuality[];
    viewer: { whepUrl: string; token: string; expiresAt: string } | null;
    configured: boolean;
}

export interface ScreenUpdatePayload {
    name?: string;
    location?: string;
    playlistId?: string | null;
    settings?: Partial<{ orientation: ScreenOrientation; volume: number; logLevel: ScreenLogLevel; audioDevice: string }>;
}

export const screensApi = {
    list: async (): Promise<ScreensResponse> => {
        const response = await apiClient.get<ScreensResponse>('/bos/screens');
        return response.data ?? { onlineWindowSec: 30, pollSec: 20, serverTime: new Date().toISOString(), screens: [] };
    },
    get: async (screenId: string): Promise<Screen> => {
        const response = await apiClient.get<Screen>(`/bos/screens/${id(screenId)}`);
        if (!response.data) throw new Error('Screen not found');
        return response.data;
    },
    update: async (screenId: string, payload: ScreenUpdatePayload): Promise<Screen> => {
        try {
            const response = await apiClient.put<Screen>(`/bos/screens/${id(screenId)}`, payload);
            return response.data!;
        } catch (error) {
            Logger.error(LOG_PREFIX, `update(${screenId}) failed: ${error}`);
            throw error;
        }
    },
    remove: async (screenId: string): Promise<void> => {
        await apiClient.delete(`/bos/screens/${id(screenId)}`);
    },
    sendCommand: async (screenId: string, type: ScreenCommandType, payload: Record<string, unknown> = {}): Promise<ScreenCommand> => {
        try {
            const response = await apiClient.post<ScreenCommand>(`/bos/screens/${id(screenId)}/commands`, { type, payload });
            return response.data!;
        } catch (error) {
            Logger.error(LOG_PREFIX, `sendCommand(${screenId}, ${type}) failed: ${error}`);
            throw error;
        }
    },
    sendBulkCommand: async (screenIds: string[], type: ScreenCommandType, payload: Record<string, unknown> = {}) => {
        const response = await apiClient.post<{ results: Array<{ screenId: string; commandId?: string; error?: string }> }>(
            '/bos/screens/commands',
            { screenIds, type, payload },
        );
        return response.data?.results ?? [];
    },
    commands: async (screenId: string): Promise<ScreenCommand[]> => {
        const response = await apiClient.get<ScreenCommand[]>(`/bos/screens/${id(screenId)}/commands`);
        return Array.isArray(response.data) ? response.data : [];
    },
    playback: async (
        screenId: string,
        options: { limit?: number; skip?: number; reel?: string; from?: string; to?: string; exportAll?: boolean } = {},
    ): Promise<{ items: PlaybackLogRow[]; total: number; configured?: boolean; message?: string }> => {
        const params = new URLSearchParams();
        params.set('limit', String(options.limit ?? 10));
        params.set('skip', String(options.skip ?? 0));
        if (options.reel) params.set('reel', options.reel);
        if (options.from) params.set('from', options.from);
        if (options.to) params.set('to', options.to);
        if (options.exportAll) params.set('export', '1');
        const response = await apiClient.get<{ items: PlaybackLogRow[]; total: number; configured?: boolean; message?: string }>(
            `/bos/screens/${id(screenId)}/playback?${params.toString()}`,
        );
        return response.data ?? { items: [], total: 0 };
    },
    clearPlayback: async (screenId: string): Promise<{ configured: boolean; deleted?: boolean; message?: string }> => {
        const response = await apiClient.delete<{ configured: boolean; deleted?: boolean; message?: string }>(`/bos/screens/${id(screenId)}/playback`);
        return response.data ?? { configured: false };
    },
    settings: async (): Promise<ScreenSettings> => {
        const response = await apiClient.get<ScreenSettings>('/bos/screen-settings');
        return response.data!;
    },
    regenerateSecret: async (): Promise<ScreenSettings> => {
        const response = await apiClient.post<ScreenSettings>('/bos/screen-settings/regenerate', {});
        return response.data!;
    },
    saveComposer: async (screenId: string, widgets: ScreenWidget[]): Promise<Screen> => {
        try {
            const response = await apiClient.put<Screen>(`/bos/screens/${id(screenId)}/composer`, { widgets });
            return response.data!;
        } catch (error) {
            Logger.error(LOG_PREFIX, `saveComposer(${screenId}) failed: ${error}`);
            throw error;
        }
    },
    weatherPreview: async (screenId: string, location = '', label = ''): Promise<WeatherPreview> => {
        const params = new URLSearchParams();
        if (location) params.set('location', location);
        if (label) params.set('label', label);
        const response = await apiClient.get<WeatherPreview>(`/bos/screens/${id(screenId)}/widgets/weather?${params.toString()}`);
        return response.data ?? { available: false, reason: 'No answer' };
    },
    stockQuotes: async (symbols: string[], refreshSec: number): Promise<StockQuotesPayload> => {
        const params = new URLSearchParams({ symbols: symbols.join(','), refreshSec: String(refreshSec) });
        const response = await apiClient.get<StockQuotesPayload>(`/bos/screens/widgets/stocks/quotes?${params.toString()}`);
        return response.data ?? { quotes: [], missing: symbols, fetchedAt: '', stale: true };
    },
    stockSearch: async (query: string): Promise<Array<{ symbol: string; name: string; exchange: string; quoteType: string }>> => {
        const response = await apiClient.get<{ hits: Array<{ symbol: string; name: string; exchange: string; quoteType: string }> }>(
            `/bos/screens/widgets/stocks/search?q=${encodeURIComponent(query)}`,
        );
        return response.data?.hits ?? [];
    },
    cricketScore: async (teams: string[], matchTypes: string[]): Promise<CricketWidgetPayload> => {
        const params = new URLSearchParams({ teams: teams.join(','), matchTypes: matchTypes.join(',') });
        const response = await apiClient.get<CricketWidgetPayload>(`/bos/screens/widgets/cricket?${params.toString()}`);
        return response.data ?? { matches: [], next: null, workerOnline: false, usage: null, config: null, expiresAt: null };
    },
    cricketTeams: async (): Promise<{ config: CricketWidgetPayload['config']; usage: CricketWidgetPayload['usage'] }> => {
        const response = await apiClient.get<{ config: CricketWidgetPayload['config']; usage: CricketWidgetPayload['usage'] }>('/bos/screens/widgets/cricket/teams');
        return response.data ?? { config: null, usage: null };
    },
    newsPreview: async (url: string, refresh = false): Promise<NewsFeedPreview> => {
        const response = await apiClient.post<NewsFeedPreview>('/bos/screens/widgets/news/preview', { url, refresh });
        return response.data!;
    },
    liveView: async (screenId: string): Promise<LiveViewState> => {
        const response = await apiClient.get<LiveViewState>(`/bos/screens/${id(screenId)}/live`);
        if (!response.data) throw new Error('Live view is not available');
        return response.data;
    },
    setLiveStream: async (screenId: string, change: { enabled?: boolean; quality?: LiveViewQuality }): Promise<LiveViewState> => {
        try {
            const response = await apiClient.put<LiveViewState>(`/bos/screens/${id(screenId)}/live`, change);
            if (!response.data) throw new Error('Live view is not available');
            return response.data;
        } catch (error) {
            Logger.error(LOG_PREFIX, `setLiveStream(${screenId}) failed: ${error}`);
            throw error;
        }
    },
    releases: async (refresh = false): Promise<ScreenClientRelease[]> => {
        const response = await apiClient.get<ScreenClientRelease[]>(`/bos/screen-client/releases${refresh ? '?refresh=1' : ''}`);
        return Array.isArray(response.data) ? response.data : [];
    },
};
